import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Upload, Camera } from "lucide-react";
import { toast } from "sonner";
import { ClientPicker } from "@/components/ClientPicker";

export const Route = createFileRoute("/tools/photos")({
  head: () => ({ meta: [{ title: "Job Site Photos" }, { name: "robots", content: "noindex" }] }),
  component: PhotosPage,
});

type Photo = {
  id: string;
  project_id: string | null;
  client_name: string | null;
  storage_path: string;
  caption: string | null;
  phase: string | null;
  taken_at: string;
  url?: string;
};
type Project = { id: string; title: string };

function PhotosPage() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [uploading, setUploading] = useState(false);
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [newCaption, setNewCaption] = useState("");
  const [newPhase, setNewPhase] = useState("in_progress");
  const [newProject, setNewProject] = useState<string>("");
  const [newClient, setNewClient] = useState("");
  const [newClientId, setNewClientId] = useState<string | null>(null);

  const load = async () => {
    const [{ data: p }, { data: pr }] = await Promise.all([
      supabase.from("job_photos").select("*").order("taken_at", { ascending: false }),
      supabase.from("projects").select("id, title").order("created_at", { ascending: false }),
    ]);
    const items = (p as Photo[]) || [];
    const withUrls = await Promise.all(items.map(async (ph) => {
      const { data } = await supabase.storage.from("job-photos").createSignedUrl(ph.storage_path, 3600);
      return { ...ph, url: data?.signedUrl };
    }));
    setPhotos(withUrls);
    setProjects((pr as Project[]) || []);
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const file of Array.from(files)) {
        const ext = file.name.split(".").pop() || "jpg";
        const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("job-photos").upload(path, file);
        if (upErr) throw upErr;
        const { error: dbErr } = await supabase.from("job_photos").insert({
          storage_path: path,
          caption: newCaption || null,
          phase: newPhase,
          project_id: newProject || null,
          client_id: newClientId,
          client_name: newClient || null,
        });
        if (dbErr) throw dbErr;
      }
      toast.success(`Uploaded ${files.length} photo(s)`);
      setNewCaption("");
      load();
    } catch (e: any) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const del = async (p: Photo) => {
    if (!confirm("Delete this photo?")) return;
    await supabase.storage.from("job-photos").remove([p.storage_path]);
    await supabase.from("job_photos").delete().eq("id", p.id);
    load();
  };

  const filtered = projectFilter === "all" ? photos : projectFilter === "none" ? photos.filter(p => !p.project_id) : photos.filter(p => p.project_id === projectFilter);

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold">Job Site Photos</h1>
        <p className="text-sm text-muted-foreground mt-1">Photo log for installs and service. Snap from your phone or upload.</p>
      </div>

      <div className="rounded-lg border border-border bg-card p-4 mb-6">
        <div className="grid gap-3 md:grid-cols-4 mb-3">
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Project</div>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={newProject} onChange={e => setNewProject(e.target.value)}>
              <option value="">— None —</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </label>
          <div className="block">
            <ClientPicker
              label="Customer (if no project)"
              clientId={newClientId}
              clientName={newClient}
              onPick={(f) => { setNewClientId(f.client_id); setNewClient(f.client_name ?? ""); }}
            />
          </div>
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Phase</div>
            <select className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" value={newPhase} onChange={e => setNewPhase(e.target.value)}>
              <option value="before">Before</option>
              <option value="prewire">Prewire</option>
              <option value="in_progress">In progress</option>
              <option value="after">After / completed</option>
              <option value="issue">Issue / callback</option>
            </select>
          </label>
          <label className="block">
            <div className="text-xs font-medium text-muted-foreground mb-1">Caption (optional)</div>
            <Input value={newCaption} onChange={e => setNewCaption(e.target.value)} placeholder="e.g. rack after cleanup" />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          <label className={`inline-flex items-center gap-2 rounded-md bg-foreground text-background px-4 py-2 text-sm font-medium cursor-pointer ${uploading ? "opacity-50" : "hover:opacity-90"}`}>
            <Camera className="h-4 w-4" /> Take photo
            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={e => onUpload(e.target.files)} disabled={uploading} />
          </label>
          <label className={`inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium cursor-pointer ${uploading ? "opacity-50" : "hover:bg-accent"}`}>
            <Upload className="h-4 w-4" /> Upload
            <input type="file" accept="image/*" multiple className="hidden" onChange={e => onUpload(e.target.files)} disabled={uploading} />
          </label>
          {uploading && <span className="text-sm text-muted-foreground self-center">Uploading…</span>}
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <button onClick={() => setProjectFilter("all")} className={`text-xs px-3 py-1 rounded-full border ${projectFilter === "all" ? "bg-foreground text-background" : "border-border hover:bg-accent"}`}>All ({photos.length})</button>
        <button onClick={() => setProjectFilter("none")} className={`text-xs px-3 py-1 rounded-full border ${projectFilter === "none" ? "bg-foreground text-background" : "border-border hover:bg-accent"}`}>Unassigned</button>
        {projects.slice(0, 10).map(p => (
          <button key={p.id} onClick={() => setProjectFilter(p.id)} className={`text-xs px-3 py-1 rounded-full border ${projectFilter === p.id ? "bg-foreground text-background" : "border-border hover:bg-accent"}`}>{p.title}</button>
        ))}
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
        {filtered.map(p => (
          <div key={p.id} className="group relative rounded-lg overflow-hidden border border-border bg-card">
            {p.url && <a href={p.url} target="_blank" rel="noreferrer"><img src={p.url} alt={p.caption || ""} className="w-full aspect-square object-cover" /></a>}
            <div className="p-2 text-xs">
              <div className="flex items-center justify-between gap-1">
                <span className="text-[10px] uppercase text-muted-foreground">{p.phase?.replace("_", " ")}</span>
                <button onClick={() => del(p)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400"><Trash2 className="h-3 w-3" /></button>
              </div>
              {p.caption && <div className="mt-1 line-clamp-2">{p.caption}</div>}
              <div className="text-[10px] text-muted-foreground mt-1">{new Date(p.taken_at).toLocaleDateString()}</div>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <div className="col-span-full rounded-lg border border-dashed border-border p-12 text-center text-muted-foreground">No photos yet.</div>}
      </div>
    </div>
  );
}
